/**
 * Test Subscription Upgrade Component
 * 
 * ⚠️ FOR DEVELOPMENT/TESTING ONLY
 * 
 * This component allows you to quickly change your subscription tier
 * for testing feature unlocks without going to DynamoDB.
 * 
 * Remove this component before production deployment!
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { useSubscription } from '../hooks/useSubscription';
import { SubscriptionTier, getTierDisplayName, getTierColor } from '../types/subscription';
import { Button } from './ui/Button';
import { useToast } from './ui/Toast';
import { Sparkles, AlertTriangle } from 'lucide-react';
import { isSubscriptionTestModeEnabled } from '../utils/subscriptionTestMode';

const Container = styled.div<{ $isElite?: boolean }>`
  position: fixed;
  top: ${props => props.$isElite ? '88px' : '90px'};
  right: 20px;
  background: white;
  border: 2px solid #fbbf24;
  border-radius: 8px;
  padding: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2), 0 0 0 2px rgba(251, 191, 36, 0.15);
  z-index: ${props => props.$isElite ? '999' : '99999'};
  max-width: 240px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  animation: slideIn 0.3s ease-out;
  font-size: 0.8rem;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }

  /* For Elite tier, ensure it doesn't overlap nav */
  ${props => props.$isElite && `
    top: 88px;
    z-index: 999;
  `}

  @media (min-width: 1200px) {
    ${props => props.$isElite && `
      top: 88px;
      right: 20px;
    `}
  }

  @media (max-width: 1199px) {
    position: fixed;
    bottom: 20px;
    top: auto;
    right: 20px;
    max-height: 300px;
  }

  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    max-width: calc(100vw - 20px);
    max-height: 40vh;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #e5e7eb;
`;

const WarningIcon = styled(AlertTriangle)`
  color: #f59e0b;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
`;

const Title = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0;
`;

const CurrentTier = styled.div`
  font-size: 0.7rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
  padding: 0.4rem;
  background: #f9fafb;
  border-radius: 4px;
`;

const TierBadge = styled.span<{ $tier: SubscriptionTier }>`
  display: inline-block;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-size: 0.65rem;
  font-weight: 600;
  background: ${props => getTierColor(props.$tier)}20;
  color: ${props => getTierColor(props.$tier)};
`;

const TierButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.75rem;
`;

const TierButton = styled.button<{ $active: boolean; $tier: SubscriptionTier }>`
  padding: 0.5rem 0.6rem;
  border: 1.5px solid ${props => props.$active ? getTierColor(props.$tier) : '#e5e7eb'};
  border-radius: 6px;
  background: ${props => props.$active ? getTierColor(props.$tier) : 'white'};
  color: ${props => props.$active ? 'white' : '#374151'};
  font-size: 0.7rem;
  font-weight: ${props => props.$active ? 600 : 500};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  text-align: left;
  
  &:hover {
    border-color: ${props => getTierColor(props.$tier)};
    background: ${props => props.$active ? getTierColor(props.$tier) : getTierColor(props.$tier)}10;
  }
`;

const CheckIcon = styled.span`
  font-size: 0.85rem;
`;

const Warning = styled.div`
  font-size: 0.65rem;
  color: #92400e;
  background: #fef3c7;
  padding: 0.5rem;
  border-radius: 4px;
  margin-top: 0.75rem;
  line-height: 1.4;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  background: none;
  border: none;
  font-size: 1rem;
  color: #6b7280;
  cursor: pointer;
  padding: 0.2rem;
  line-height: 1;
  
  &:hover {
    color: #1f2937;
  }
`;

export const TestSubscriptionUpgrade: React.FC = () => {
  const [isVisible, setIsVisible] = useState(true);
  const { userProfile, updateUserProfile } = useAWSAuth();
  const { tier } = useSubscription();
  const { showToast } = useToast();
  
  const tiers: SubscriptionTier[] = ['plus', 'pro', 'elite'];

  const handleTierChange = async (newTier: SubscriptionTier) => {
    try {
      if (updateUserProfile && userProfile) {
        await updateUserProfile({
          ...userProfile,
          subscriptionTier: newTier,
        });
        showToast(`Subscription tier changed to ${getTierDisplayName(newTier)}`, 'success');
      }
    } catch (error) {
      console.error('Failed to update subscription tier', error);
      showToast('Failed to update subscription tier', 'error');
    }
  };

  if (!isVisible || !isSubscriptionTestModeEnabled()) {
    return null;
  }

  // Use tier for positioning adjustments only, not for visibility
  const isElite = tier === 'elite';

  return (
    <Container $isElite={isElite}>
      <CloseButton onClick={() => setIsVisible(false)}>×</CloseButton>
      <Header>
        <WarningIcon size={16} />
        <Title>⚠ Test Mode</Title>
      </Header>
      
      <CurrentTier>
        <div style={{ marginBottom: '0.2rem' }}>Current: {getTierDisplayName(tier)}</div>
        <TierBadge $tier={tier}>{tier.toUpperCase()}</TierBadge>
        <div style={{ marginTop: '0.4rem', fontSize: '0.65rem', color: '#9ca3af' }}>
          NODE_ENV: {process.env.NODE_ENV || 'development'}
        </div>
      </CurrentTier>
      
      <TierButtons>
        {tiers.map((tierOption) => (
          <TierButton
            key={tierOption}
            $active={tier === tierOption}
            $tier={tierOption}
            onClick={() => handleTierChange(tierOption)}
          >
            {tier === tierOption && <CheckIcon>✓</CheckIcon>}
            {getTierDisplayName(tierOption)}
          </TierButton>
        ))}
      </TierButtons>
      
      <Warning>
        ▲ Development only. Remove before production!
      </Warning>
    </Container>
  );
};
